// # Field · Label · Input · Textarea · Select — 적고 고르는 칸의 한 모양(docs/design.md)
//
// `Field` 가 이름표·설명·오류를 칸과 **잇는다**. 칸(`Input`·`Textarea`·`Select`)은 가장 가까운
// `Field` 에서 id·`aria-describedby`·`aria-invalid`·`required` 를 받아 적는다 — 화면마다 id 를
// 손으로 짝지으면 언젠가 하나가 어긋나고, 스크린리더는 이름 없는 칸을 읽는다.
//
// - 칸이 직접 적은 값(`id`·`aria-describedby` …)이 `Field` 의 값보다 앞선다.
// - 오류는 `role="alert"` 로 한 번 읽힌다. 설명은 오류가 있어도 남는다(둘 다 describedby).
// - `Textarea` 의 자동 높이는 CSS `field-sizing: content` 다. 안 되는 엔진에서는 손잡이로 늘린다 —
//   JS 로 높이를 재면 한글 조합 중에 칸이 떨린다.
// - `Select` 는 **네이티브** `<select>` 를 다시 그린다. 목록은 OS 가 띄우므로 전화에서도 손에 맞고
//   키보드·타이핑 찾기가 그대로다. 화살표 하나만 우리가 그린다.
//
// 기본값은 `??` 로 준다(React Compiler, AGENTS.md §2-2).

import { ChevronDown } from 'lucide-react'
import {
  createContext,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useContext,
  useId,
} from 'react'
import { cx } from './cx.ts'

export type ControlSize = 'sm' | 'md' | 'lg'

interface FieldLink {
  id: string
  described: string | undefined
  invalid: boolean
  required: boolean
}

const FieldContext = createContext<FieldLink | null>(null)

export interface FieldProps {
  /** 칸의 이름. 보이는 이름표가 된다. */
  label: ReactNode
  /** 이름표 아래 한 줄 설명. */
  hint?: ReactNode
  /** 있으면 칸이 틀렸다고 표시되고 이 문장이 읽힌다. */
  error?: ReactNode
  /** 칸에 `required` 를 싣고 이름표에 표시를 붙인다. */
  required?: boolean
  /** 칸의 id. 없으면 만든다. */
  id?: string
  className?: string
  /** 칸 하나(`Input`·`Textarea`·`Select`). */
  children: ReactNode
}

export function Field(props: FieldProps) {
  const made = useId()
  const id = props.id ?? made
  const hint_id = props.hint ? `${id}-hint` : undefined
  const error_id = props.error ? `${id}-error` : undefined
  const described = [hint_id, error_id].filter(Boolean).join(' ') || undefined
  const link: FieldLink = {
    id,
    described,
    invalid: Boolean(props.error),
    required: props.required === true,
  }
  return (
    <div className={cx('Field', props.className)} data-invalid={link.invalid || undefined}>
      <Label htmlFor={id} required={link.required}>
        {props.label}
      </Label>
      {props.hint && (
        <p className="Field-hint" id={hint_id}>
          {props.hint}
        </p>
      )}
      <FieldContext.Provider value={link}>{props.children}</FieldContext.Provider>
      {props.error && (
        <p className="Field-error" id={error_id} role="alert">
          {props.error}
        </p>
      )}
    </div>
  )
}

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  /** 참이면 이름 뒤에 필수 표시(스크린리더에는 칸의 `required` 가 알린다). */
  required?: boolean
}

export function Label(props: LabelProps) {
  const { required, className, children, htmlFor, ...rest } = props
  return (
    <label {...rest} htmlFor={htmlFor} className={cx('Label', className)}>
      {children}
      {required && (
        <span className="Label-required" aria-hidden>
          *
        </span>
      )}
    </label>
  )
}

/** 칸이 적은 값이 먼저, 없으면 `Field` 의 것. */
interface Linked {
  id?: string | undefined
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling' | undefined
  required?: boolean | undefined
}

function useLinked(own: Linked): Linked {
  const link = useContext(FieldContext)
  if (!link) return own
  return {
    id: own.id ?? link.id,
    'aria-describedby': own['aria-describedby'] ?? link.described,
    'aria-invalid': own['aria-invalid'] ?? (link.invalid || undefined),
    required: own.required ?? (link.required || undefined),
  }
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 기본 `md`(32px). */
  size?: ControlSize
  /**
   * 보내는 중. `disabled` 가 아니라 `readOnly` 로 잠근다 — Enter 로 보낸 칸이 꺼지면 초점이 바닥에 떨어져, 틀린 코드를
   * 넣은 사람이 오류를 보고 칸을 다시 눌러야 했다(3차 F304). 보내기는 단추의 `loading` 이 막는다.
   */
  busy?: boolean
  ref?: Ref<HTMLInputElement>
}

export function Input(props: InputProps) {
  const { size, className, id, required, busy, ...rest } = props
  const linked = useLinked({
    id,
    required,
    'aria-describedby': rest['aria-describedby'],
    'aria-invalid': rest['aria-invalid'],
  })
  return (
    <input
      type="text"
      // 브라우저가 예전에 친 아무 말(다른 사이트의 검색어까지)을 칸 밑에 띄우지 않는다(3차 F168).
      // 로그인·비밀번호 칸은 제 값(`email`·`current-password` …)을 넘겨 덮는다.
      autoComplete="off"
      {...rest}
      {...linked}
      readOnly={busy || rest.readOnly}
      aria-busy={busy || undefined}
      className={cx('Input', className)}
      data-size={size ?? 'md'}
    />
  )
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** 기본 `md`. 입력칸과 같은 글자 크기·안쪽 여백 단계다(G18). */
  size?: ControlSize
  ref?: Ref<HTMLTextAreaElement>
}

export function Textarea(props: TextareaProps) {
  const { className, id, required, size, ...rest } = props
  const linked = useLinked({
    id,
    required,
    'aria-describedby': rest['aria-describedby'],
    'aria-invalid': rest['aria-invalid'],
  })
  return (
    <textarea
      rows={3}
      {...rest}
      {...linked}
      className={cx('Textarea', className)}
      data-size={size ?? 'md'}
    />
  )
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** 기본 `md`(32px). */
  size?: ControlSize
  /**
   * 고른 값을 저장하는 중. `disabled` 와 달리 초점을 지킨다 — 초점이 앉은 칸이 `disabled` 가 되면 브라우저가
   * 초점을 문서 바닥으로 떨어뜨린다(3차 F302, 단추의 `loading` 과 같은 까닭). 도는 동안 바꾸기는 무시한다.
   */
  busy?: boolean
  ref?: Ref<HTMLSelectElement>
}

export function Select(props: SelectProps) {
  const { size, className, id, required, children, busy, onChange, ...rest } = props
  const linked = useLinked({
    id,
    required,
    'aria-describedby': rest['aria-describedby'],
    'aria-invalid': rest['aria-invalid'],
  })
  const s = size ?? 'md'
  return (
    <span className={cx('Select', className)} data-size={s}>
      <select
        {...rest}
        {...linked}
        aria-disabled={busy || rest['aria-disabled']}
        aria-busy={busy || undefined}
        onChange={(e) => {
          if (busy !== true) onChange?.(e)
        }}
      >
        {children}
      </select>
      <ChevronDown className="Select-chevron" size={s === 'sm' ? 14 : 16} aria-hidden />
    </span>
  )
}
